import { CollectionViewer, SelectionChange } from '@angular/cdk/collections';
import { FlatTreeControl } from '@angular/cdk/tree';
import { NodeDto, TreeNode, TreeNodeState } from '@app/features/hierarchy/models/hierarchy.model';
import { Page } from '@app/shared/models/page.interface';
import { BehaviorSubject, merge, Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

export abstract class TreeDataSource {
  treeChangesSubscription: Subscription;

  // Tree cache
  dataTree = new Map<string, TreeNode>();

  // Drawn tree
  dataChange = new BehaviorSubject<TreeNode[]>([]);

  get data(): TreeNode[] {
    return this.dataChange.value;
  }
  set data(value: TreeNode[]) {
    this.treeControl.dataNodes = value;
    this.dataChange.next(value);
  }

  constructor(protected treeControl: FlatTreeControl<TreeNode>) {}

  /**
   * Method to initialize the data source.
   * Load the root node and its children.
   *
   * @param rootNode root node
   */
  async initialize(rootNode: NodeDto) {
    const rootTreeNode = new TreeNode(rootNode, 0, rootNode.childrenCount, false, false, null);
    this.dataTree.set(rootNode.id, rootTreeNode);

    await this.getPage(rootTreeNode.item.hierarchyId, rootTreeNode.item.id, rootTreeNode.state)
      .toPromise()
      .then(response => {
        response.content.forEach((nodeDto, i) => {
          const isLoadMore =
            i + 1 === response.content.length &&
            response.totalElements > response.pageable.pageNumber * response.pageable.pageSize + i + 1;
          const state = isLoadMore ? new TreeNodeState() : null;
          this.dataTree.set(nodeDto.id, new TreeNode(nodeDto, 1, nodeDto.childrenCount, false, isLoadMore, state));
        });
        this.data = [...this.dataTree.values()];
        this.treeControl.expand(rootTreeNode); // FIXME: teview this expand
        this.treeControl.collapse(rootTreeNode); // FIXME: teview this expand
        this.treeControl.expand(rootTreeNode); // FIXME: teview this expand
      });
  }

  /**
   * Method to initialize the data source from a search text.
   * Load the root node and its children from a search text.
   *
   * @param rootNode root node
   * @param filter filter
   */
  async initializeWithSearch(rootNode: NodeDto, filter: string) {
    await this.getHierarchyNodesRecursive(rootNode.hierarchyId, filter)
      .toPromise()
      .then(foundNodes => {
        if (foundNodes.length > 0) {
          foundNodes.forEach(nodeDto => {
            nodeDto.childrenCount = this.getChildrenCountFromTotal(nodeDto.id, foundNodes);
            this.dataTree.set(
              nodeDto.id,
              new TreeNode(nodeDto, this.getNodeLevel(nodeDto, foundNodes), nodeDto.childrenCount, false, false, null)
            );
          });
          this.dataTree.forEach(nodeTree => {
            if (this.findChildren(nodeTree).length > 0) {
              this.treeControl.expand(nodeTree);
            }
          });
          this.data = [...this.dataTree.values()];
        }
      });
  }

  async initializeWithNodes(nodes: NodeDto[]) {
    if (nodes.length > 0) {
      nodes.forEach(nodeDto => {
        nodeDto.childrenCount = this.getChildrenCountFromTotal(nodeDto.id, nodes);
        this.dataTree.set(
          nodeDto.id,
          new TreeNode(nodeDto, this.getNodeLevel(nodeDto, nodes), nodeDto.childrenCount, false, false, null)
        );
      });
      this.data = [...this.dataTree.values()];
      this.treeControl.expandAll();
    }
  }

  protected getChildrenCountFromTotal(parentId: string, totalNodes: NodeDto[]): number {
    return totalNodes.reduce(
      (accumulator, nodeDto) => (nodeDto.parentId === parentId ? accumulator + 1 : accumulator),
      0
    );
  }

  connect(collectionViewer: CollectionViewer): Observable<TreeNode[]> {
    if (!this.treeChangesSubscription) {
      this.treeChangesSubscription = this.treeControl.expansionModel.changed.subscribe(change => {
        if (change.added || change.removed) {
          this.handleTreeControl(change);
        }
      });
    }

    return merge(collectionViewer.viewChange, this.dataChange).pipe(map(() => this.data));
  }

  handleTreeControl(change: SelectionChange<TreeNode>) {
    if (change.added) {
      change.added.forEach(node => this.toggleNode(node, true));
    }
    if (change.removed) {
      change.removed
        .slice()
        .reverse()
        .forEach(node => this.toggleNode(node, false));
    }
  }

  /**
   * Method to expand and collapse a node.
   * When a node is expanded it can be loaded from the backend or from cache.
   *
   * @param node node
   * @param expand expanded or collapse
   */
  async toggleNode(node: TreeNode, expand: boolean) {
    // First time the root node is loaded in initialize methods
    if (expand) {
      node.isLoading = true;

      let nodes: TreeNode[];
      // From cache
      if (this.findChildren(node).length > 0) {
        nodes = this.findChildren(node);
        const index = this.treeInsertionIndex(node);
        this.data.splice(index + 1, 0, ...nodes);

        node.isLoading = false;
        this.dataChange.next(this.data);

        this.loadExpandedNodes(nodes);
      } else {
        nodes = await this.getPage(node.item.hierarchyId, node.item.id, null)
          .toPromise()
          .then(response => {
            return response.content.map((nodeDto, i) => {
              const isLoadMore: boolean =
                i + 1 === response.content.length &&
                response.totalElements > response.pageable.pageNumber * response.pageable.pageSize + i + 1;
              const state = isLoadMore ? new TreeNodeState() : null;
              return new TreeNode(nodeDto, node.level + 1, nodeDto.childrenCount, false, isLoadMore, state);
            });
          });

        nodes.forEach(nodeElement => this.dataTree.set(nodeElement.item.id, nodeElement));
        const index = this.treeInsertionIndex(node);
        this.data.splice(index + 1, 0, ...nodes);

        node.isLoading = false;
        this.dataChange.next(this.data);
      }
    } else {
      const index = this.data.indexOf(node);
      let count = 0;
      for (let i = index + 1; i < this.data.length && this.data[i].level > node.level; i++) {
        count++;
      }
      this.data.splice(index + 1, count);

      this.dataChange.next(this.data);
    }
  }

  /**
   * Method recursive to expand the nodes from the information stored in the cache.
   *
   * @param nodes list of nodes to expand
   */
  loadExpandedNodes(nodes: TreeNode[]) {
    for (let node of nodes) {
      if (this.treeControl.isExpanded(node)) {
        const children: TreeNode[] = this.findChildren(node);

        const index = this.treeInsertionIndex(node);
        this.data.splice(index + 1, 0, ...children);
        this.dataChange.next(this.data);

        // Recursive
        this.loadExpandedNodes(children);
      }
    }
  }

  /**
   * Method to control the pagination of a node.
   *
   * @param node node
   */
  async loadMore(node: TreeNode) {
    node.isLoading = true;

    const parent = this.findParent(node);
    const state = node.state;
    state.page = state.page + 1;

    const nodes: TreeNode[] = await this.getPage(node.item.hierarchyId, parent.item.id, state)
      .toPromise()
      .then(response => {
        return response.content.map((nodeDto, i) => {
          const isLoadMore: boolean =
            i + 1 === response.content.length &&
            response.totalElements > response.pageable.pageNumber * response.pageable.pageSize + i + 1;
          const newState = isLoadMore ? this.getNewTreeNodeState(state.page) : null;
          return new TreeNode(nodeDto, node.level, nodeDto.childrenCount, false, isLoadMore, newState);
        });
      });

    nodes.forEach(nodeElement => this.dataTree.set(nodeElement.item.id, nodeElement));
    const index = this.treeInsertionIndex(node);
    this.data.splice(index + 1, 0, ...nodes);

    const dataTreeNode = this.dataTree.get(node.item.id);
    dataTreeNode.isLoadMore = false;
    dataTreeNode.state = null;

    const dataNode = this.data.find(dataNodeElement => dataNodeElement.item.id === node.item.id);
    dataNode.isLoadMore = false;
    dataNode.state = null;

    node.isLoading = false;
    this.dataChange.next(this.data);
  }

  getNewTreeNodeState(page: number) {
    const nodeState = new TreeNodeState();
    nodeState.page = page;
    return nodeState;
  }

  getNodeLevel(node: NodeDto, nodes: NodeDto[]): number {
    if (node.parentId == null) {
      return 0;
    }
    const parentNode = nodes.find(n => n.id === node.parentId);
    if (parentNode == null) {
      console.error('Something went wrong. Parent node not found.');
      return 0;
    }
    return 1 + this.getNodeLevel(parentNode, nodes);
  }

  treeInsertionIndex(node: TreeNode) {
    let index = -1;
    this.data.forEach((dataNode, i) => {
      if (dataNode.item.id === node.item.id || dataNode.item.parentId === node.item.id) {
        index = i;
      }
    });

    if (this.data[index] && this.data[index].item.id !== node.item.id) {
      const auxIndex = this.treeInsertionIndex(this.data[index]);
      if (auxIndex !== -1) {
        index = auxIndex;
      }
    }

    return index;
  }

  findNodeById(id: string): TreeNode {
    let node = null;
    for (let dataNode of this.dataTree.values()) {
      if (dataNode.item.id === id) {
        node = dataNode;
        break;
      }
    }
    return node;
  }

  findParent(child: TreeNode): TreeNode {
    let parent = null;
    for (let node of this.dataTree.values()) {
      if (node.item.id === child.item.parentId) {
        parent = node;
        break;
      }
    }
    return parent;
  }

  findChildren(parentNode: TreeNode): TreeNode[] {
    const children: TreeNode[] = [];
    for (let childNode of this.dataTree.values()) {
      if (childNode.item.parentId === parentNode.item.id) {
        children.push(childNode);
      }
    }
    return children;
  }

  /**
   * Method to move a list of nodes.
   *
   * @param nodesDto list of nodes
   * @param targetNodeDto target parent node
   */
  moveNodes(hierarchyId: string, childrenList: string[], targetNodeId: string) {
    childrenList.forEach(id => {
      const node = this.findNodeById(id);
      this.deleteNode(node);
    });

    const parentNode = this.findNodeById(targetNodeId);
    parentNode.item.childrenCount++;
    parentNode.childrenCount++;
    this.dataTree.set(parentNode.item.id, parentNode);
    const index = this.data.indexOf(parentNode);
    this.data[index].item.childrenCount++;
    this.data[index].childrenCount++;
    this.dataChange.next(this.data);

    const children = this.findChildren(parentNode);
    children.forEach(node => this.deleteNode(node));

    if (this.treeControl.isExpanded(parentNode)) {
      const state = children.length > 0 ? children[children.length - 1].state : null;

      this.getPage(hierarchyId, parentNode.item.id, state).subscribe(response => {
        parentNode.childrenCount = response.numberOfElements;
        response.content.forEach((nodeDto, i) => {
          const isLoadMore: boolean =
            i + 1 === response.content.length &&
            response.totalElements > response.pageable.pageNumber * response.pageable.pageSize + i + 1;
          const state = isLoadMore ? new TreeNodeState() : null;
          const treeNode = new TreeNode(nodeDto, parentNode.level + 1, nodeDto.childrenCount, false, false, state);
          this.createNode(treeNode, parentNode);
        });
      });
    }
  }

  /**
   * Method to create a node.
   *
   * @param node node
   * @param parentNode parent node
   */
  createNode(node: TreeNode, parentNode: TreeNode) {
    const index = this.data.indexOf(parentNode);
    if (index > -1) {
      this.data[index].item.childrenCount++;
      this.data[index].childrenCount++;

      parentNode.item.childrenCount++;
      parentNode.childrenCount++;
      this.dataTree.set(parentNode.item.id, parentNode);

      this.dataTree.set(node.item.id, node);
      if (this.treeControl.isExpanded(this.data[index])) {
        let count = 0;
        for (let i = index + 1; i < this.data.length && this.data[i].level > parentNode.level; i++) {
          count++;
        }
        node.level = parentNode ? parentNode.level + 1 : 1;
        this.data.splice(index + count + 1, 0, node);
        this.dataChange.next(this.data);
      }
    }
  }

  /**
   * Method to delete a node.
   *
   * @param node node
   */
  deleteNode(node: TreeNode): TreeNode[] {
    const deletedNodes: TreeNode[] = [];

    if (node) {
      deletedNodes.push(node);
      this.dataTree.delete(node.item.id);

      const parentNode = this.findParent(node);
      if (parentNode) {
        const parentIndex = this.data.indexOf(parentNode);
        this.data[parentIndex].item.childrenCount--;
        this.data[parentIndex].childrenCount--;
      }

      const children: TreeNode[] = this.findChildren(node);
      children.forEach(nodeElement => {
        deletedNodes.push(nodeElement);
        this.dataTree.delete(nodeElement.item.id);
      });

      const index = this.data.indexOf(node);
      if (index > -1) {
        let count = 0;
        for (let i = index + 1; i < this.data.length && this.data[i].level > node.level; i++) {
          count++;
        }
        this.data.splice(index, count + 1);
        this.dataChange.next(this.data);
      }
    }

    return deletedNodes;
  }

  protected abstract getPage(hierarchyId: string, nodeId: string, state: TreeNodeState): Observable<Page<NodeDto>>;

  protected abstract getHierarchyNodesRecursive(hierarchyId: string, filter: string): Observable<NodeDto[]>;
}
